import { Box, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { errorNotificationIcon } from '@/theme/notificationIcons';

// Mora se poklapati s backend OrganisationService.ALLOWED_LOGO_CONTENT_TYPES/MAX_LOGO_SIZE_BYTES
// i s orgForm.logoHint kopijom ispod (PNG/JPG, max 2MB) — provjera ovdje je samo da korisnik
// ne čeka nepotreban upload da bi tek onda saznao da je datoteka odbijena.
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg'];
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ALLOWED_LOGO_TYPES.join(',');

interface LogoPickerFieldProps {
  initials: string;
  src?: string | null;
  label: string;
  labelMobile: string;
  hint: string;
  hintMobile: string;
  onFileSelected: (file: File) => void;
  disabled?: boolean;
}

export const LogoPickerField = ({
  initials,
  src,
  label,
  labelMobile,
  hint,
  hintMobile,
  onFileSelected,
  disabled
}: LogoPickerFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => inputRef.current?.click();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      notifications.show({
        color: 'red',
        icon: errorNotificationIcon,
        message: t('orgForm.logoInvalidType')
      });
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      notifications.show({
        color: 'red',
        icon: errorNotificationIcon,
        message: t('orgForm.logoTooLarge')
      });
      return;
    }

    onFileSelected(file);
  };

  return (
    <Group gap={20} align="center" wrap="nowrap">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_LOGO_TYPES}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Box
        w={{ base: 52, sm: 64 }}
        h={{ base: 52, sm: 64 }}
        style={{
          borderRadius: 12,
          backgroundColor: 'var(--color-bg-surface-3)',
          border: '1px solid var(--color-border-default)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        {src ? (
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Text fz={{ base: 16, sm: 20 }} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {initials}
          </Text>
        )}
      </Box>
      <Stack gap={6}>
        <Text
          component="button"
          type="button"
          onClick={handleClick}
          disabled={disabled}
          fz={13.5}
          fw={600}
          hiddenFrom="sm"
          style={{
            color: 'var(--color-accent-primary-active)',
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            textAlign: 'left'
          }}
        >
          {labelMobile}
        </Text>
        <Text
          component="button"
          type="button"
          onClick={handleClick}
          disabled={disabled}
          fz={13.5}
          fw={600}
          visibleFrom="sm"
          style={{
            color: 'var(--color-accent-primary-active)',
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            textAlign: 'left'
          }}
        >
          {label}
        </Text>
        <Text fz={11} hiddenFrom="sm" style={{ color: 'var(--color-text-muted)' }}>
          {hintMobile}
        </Text>
        <Text fz={11} visibleFrom="sm" style={{ color: 'var(--color-text-muted)' }}>
          {hint}
        </Text>
      </Stack>
    </Group>
  );
};
